"use client";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { GLASS_CARD } from "@/components/glass-card";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { ImageZoom } from "@/components/ui/shadcn-io/image-zoom";
import { ArrowLeft, ExternalLink, Calendar, Star } from "lucide-react";
import { Link } from "@/i18n/navigation";
import Image from "next/image";
import { components } from "@/openapi/generated";
import { MarkdownRenderer } from "@/components/markdown-renderer";
import {
  formatShortDate,
  formatRelativeDate,
  formatImportance,
  formatCategoryName,
} from "@/app/[locale]/release-notes/util/release-note-formatters";
import {
  CATEGORY_COLORS,
  CATEGORY_ICONS,
} from "@/app/[locale]/release-notes/util/release-note-constants";
import { useTranslations } from "next-intl";

type ReleaseNoteDetail = components["schemas"]["ReleaseNoteDetailDto"];

interface ReleaseNoteDetailViewProps {
  releaseNote: ReleaseNoteDetail;
  className?: string;
}

export function ReleaseNoteDetailView({
  releaseNote,
  className
}: ReleaseNoteDetailViewProps) {
  const t = useTranslations("pages");

  const {
    title,
    summary,
    content,
    category,
    importance,
    publishedAt,
    prId,
    prMergedAt,
    prCreatedAt,
    aiProcessedAt,
    images,
    relatedLinks,
    aiModel,
    aiTotalTokens,
  } = releaseNote;

  // Safety check for publishedAt (optional in API due to unpublished notes)
  if (!publishedAt) {
    return <div>{t("releaseNotes.notFound")}</div>;
  }

  const displayDate = prMergedAt || publishedAt;
  const categoryColor = CATEGORY_COLORS[category];
  const categoryIcon = CATEGORY_ICONS[category];
  const categoryName = formatCategoryName(category);

  return (
    <div className={className}>
      {/* Navigation */}
      <div className="mb-6">
        <Button variant="ghost" asChild className="mb-4">
          <Link href="/release-notes">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t("releaseNotes.backToOverview")}
          </Link>
        </Button>

        {/* Breadcrumb could go here */}
        <div className="text-sm text-muted-foreground">
          <Link href="/release-notes" className="hover:underline">
            {t("releaseNotes.breadcrumb")}
          </Link>
          {" › "}
          <span>{title}</span>
        </div>
      </div>

      <article className="space-y-8">
        {/* Header */}
        <header className="space-y-4">
          <div className="flex items-center gap-3 text-sm">
            <Badge className={categoryColor}>
              <span className="mr-1">{categoryIcon}</span>
              {categoryName}
            </Badge>

            <time
              className="text-muted-foreground"
              dateTime={displayDate}
            >
              { }
              {formatShortDate(displayDate) ?? ""} • {formatRelativeDate(displayDate)}
            </time>
          </div>

          <h1 className="text-3xl font-bold tracking-tight lg:text-4xl">
            {title}
          </h1>

          <p className="text-lg text-muted-foreground leading-relaxed">
            {summary}
          </p>

          {/* Metadata bar */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            {importance && (
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4" />
                <span title={t("releaseNotes.importanceLabel", { importance })}>
                  {formatImportance(importance)}
                </span>
              </div>
            )}

            {prId && (
              <div className="flex items-center gap-2">
                <a
                  href={`https://github.com/donista-org/donista/pull/${prId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 hover:underline"
                >
                  {t("releaseNotes.pullRequestLink", { id: prId })}
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}
          </div>
        </header>

        <Separator />

        {/* Content */}
        {content && (
          <MarkdownRenderer
            content={content}
            className="prose-lg dark:prose-invert"
          />
        )}

        {/* Images */}
        {images && images.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">{t("releaseNotes.imagesHeading")}</h2>
            <div className="grid gap-4 md:grid-cols-2">
              {images.map((image, index) => (
                image.url && (
                  <Card key={index}>
                    <CardContent className="p-4">
                      <ImageZoom zoomMargin={50}>
                        <Image
                          src={image.url}
                          alt={image.alt || image.caption || t("releaseNotes.imageFallbackAlt", { index: index + 1 })}
                          width={800}
                          height={450}
                          className="w-full h-auto object-cover rounded-lg"
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                          unoptimized
                        />
                      </ImageZoom>

                      {image.caption && (
                        <p className="mt-2 text-sm text-muted-foreground">
                          {image.caption}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )
              ))}
            </div>
          </div>
        )}

        {/* Related Links */}
        {relatedLinks && relatedLinks.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">{t("releaseNotes.relatedLinksHeading")}</h2>
            <div className="grid gap-3">
              {relatedLinks.map((link, index) => (
                link.url && link.title && (
                  <div key={index} className={cn(GLASS_CARD, "flex items-start justify-between")}>
                    <div className="space-y-1">
                      <h3 className="font-medium">{link.title}</h3>
                      {link.description && (
                        <p className="text-sm text-muted-foreground">
                          {link.description}
                        </p>
                      )}
                    </div>
                    <Button variant="ghost" size="sm" asChild>
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-shrink-0"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </Button>
                  </div>
                )
              ))}
            </div>
          </div>
        )}

        <Separator />

        {/* Metadata Footer */}
        <footer className="space-y-4 text-sm text-muted-foreground">
          <div className="grid gap-2 md:grid-cols-2">
            {prCreatedAt && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{t("releaseNotes.createdAt", { date: formatShortDate(prCreatedAt) ?? "" })}</span>
              </div>
            )}

            {prMergedAt && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{t("releaseNotes.mergedLabel")} {formatShortDate(prMergedAt) ?? ""}</span>
              </div>
            )}
          </div>

          {(aiModel || aiTotalTokens || aiProcessedAt) && (
            <div className="pt-2 border-t text-xs space-y-1">
              <p>
                {t("releaseNotes.aiGenerated")}
              </p>
              {aiProcessedAt && (
                <p>
                  {t("releaseNotes.aiGeneratedAt", { date: formatShortDate(aiProcessedAt) ?? "" })}
                </p>
              )}
            </div>
          )}
        </footer>
      </article>
    </div>
  );
}
